import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaTrash, FaCheck } from "react-icons/fa";
import { IoPeopleOutline } from "react-icons/io5";
import {
  TbBrandInstagram,
  TbBrandWhatsapp,
  TbBrandTelegram,
  TbChevronRight,
  TbChevronLeft,
  TbChevronDown,
} from "react-icons/tb";
import { MdBlock } from "react-icons/md";
import { IoAddCircleOutline } from "react-icons/io5";
import {
  deleteExcludeClient,
} from "../../api/exludeClients/excludeClientService";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/useRedux";
import {
  selectExcludeClientList,
  selectExcludeClientLoading,
  setDeleteExcludeClient,
} from "../../redux/slices/excludeClientSlice/ExcludeClientSlice";
import { getExcludeProfilesThunk } from "../../redux/slices/excludeClientSlice/asyncthunk";
import ExcludeClientsModal from "./ExcludeClientsModal";


const platformConfig = {
  instagram: {
    icon: <TbBrandInstagram className="w-4 h-4 text-pink-600" />,
    label: "Instagram",
    bg: "bg-pink-50 border-pink-200 text-pink-700",
  },
  whatsapp: {
    icon: <TbBrandWhatsapp className="w-4 h-4 text-emerald-600" />,
    label: "WhatsApp",
    bg: "bg-emerald-50 border-emerald-200 text-emerald-700",
  },
  telegram: {
    icon: <TbBrandTelegram className="w-4 h-4 text-blue-600" />,
    label: "Telegram",
    bg: "bg-blue-50 border-blue-200 text-blue-700",
  },
};

function ConfirmDeleteModal({ client, onConfirm, onCancel }) {
  const displayName =
    client.excluded_username ||
    client.excluded_phone_number ||
    "this client";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4"
      >
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
          <FaTrash className="w-5 h-5 text-red-500" />
        </div>
        <h3 className="text-center text-lg font-semibold text-gray-900 mb-1">
          Remove from excluded?
        </h3>
        <p className="text-center text-sm text-gray-500 mb-6">
          <span className="font-medium text-gray-700">{displayName}</span> will
          be removed from your excluded list.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
          >
            Remove
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function ExcludedClients() {
  const dispatch = useAppDispatch();
  const clients = useAppSelector(selectExcludeClientList);
  const loading = useAppSelector(selectExcludeClientLoading);

  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [confirmClient, setConfirmClient] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showRowsDropdown, setShowRowsDropdown] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const rowsDropdownRef = useRef(null);

  useEffect(() => {
    dispatch(getExcludeProfilesThunk());
  }, [dispatch]);

  useEffect(() => {
    const handle = (e) => {
      if (
        rowsDropdownRef.current &&
        !rowsDropdownRef.current.contains(e.target)
      ) {
        setShowRowsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleDelete = async (client) => {
    try {
      setDeletingId(client.id);
      await deleteExcludeClient({ id: client.id, platform: client.platform });
      dispatch(setDeleteExcludeClient(client.id));
    } catch (err) {
      console.error("Failed to delete excluded client:", err);
    } finally {
      setDeletingId(null);
      setConfirmClient(null);
    }
  };

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return clients;
    const q = searchQuery.toLowerCase();
    return clients.filter(
      (c) =>
        (c.excluded_username || "").toLowerCase().includes(q) ||
        (c.excluded_phone_number || "").toLowerCase().includes(q),
    );
  }, [clients, searchQuery]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const handleChangePage = (p) => {
    if (p >= 1 && p <= totalPages) setPage(p);
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, rowsPerPage]);

  const rowsOptions = [5, 10, 20, 50];

  const getDisplayName = (c) =>
    c.excluded_username ||
    c.excluded_phone_number ||
    "—";

  const getSubLabel = (c) => {
    if (c.platform === "whatsapp") {
      return c.excluded_phone_number || null;
    }
    return c.excluded_username || null;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" },
    },
  };

  return (
    <motion.main
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 w-full overflow-x-auto min-h-screen"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-50 rounded-xl border border-red-100">
              <MdBlock className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Excluded contacts
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">
                {filtered.length} contact{filtered.length !== 1 ? "s" : ""}{" "}
                excluded
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative w-full sm:max-w-xs">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search excluded clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-700 transition-colors shrink-0"
            >
              <IoAddCircleOutline className="w-4 h-4" />
              Excluded contacts
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Table */}
      <motion.div variants={itemVariants}>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  {["Client", "Platform", "Username / Phone", "Action"].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-16 text-center">
                      <div className="flex justify-center">
                        <div className="w-8 h-8 border-4 border-red-400 border-t-transparent rounded-full animate-spin" />
                      </div>
                    </td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center text-gray-400">
                        <IoPeopleOutline className="w-12 h-12 mb-3 text-gray-300" />
                        <p className="text-base font-medium text-gray-500">
                          {searchQuery
                            ? "No customers match your search."
                            : "No excluded contacts yet."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((client, index) => {
                    const platform = platformConfig[client.platform];
                    const subLabel = getSubLabel(client);

                    return (
                      <motion.tr
                        key={client.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: index * 0.04 }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        {/* Client */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="relative shrink-0">
                              <img
                                src={client.image_url}
                                alt={getDisplayName(client)}
                                className="w-10 h-10 rounded-full border border-gray-200 object-cover grayscale opacity-70"
                              />
                             
                            </div>
                            <span className="font-medium text-gray-700 text-sm">
                              {getDisplayName(client)}
                            </span>
                          </div>
                        </td>

                        {/* Platform */}
                        <td className="px-6 py-4">
                          {platform ? (
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${platform.bg}`}
                            >
                              {platform.icon}
                              {platform.label}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>

                        {/* Username / Phone */}
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-500">
                            {subLabel || "—"}
                          </span>
                        </td>

                        {/* Action */}
                        <td className="px-6 py-4">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setConfirmClient(client)}
                            disabled={deletingId === client.id}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-red-500 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove from excluded"
                          >
                            {deletingId === client.id ? (
                              <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FaTrash className="w-3.5 h-3.5" />
                            )}
                            Remove
                          </motion.button>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-5 pt-4 border-t border-gray-100 gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Rows per page:</span>
              <div className="relative" ref={rowsDropdownRef}>
                <button
                  onClick={() => setShowRowsDropdown((v) => !v)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white hover:border-gray-300 min-w-[110px] justify-between"
                >
                  <span>{rowsPerPage} per page</span>
                  <TbChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${showRowsDropdown ? "rotate-180" : ""}`}
                  />
                </button>
                <AnimatePresence>
                  {showRowsDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute bottom-full left-0 mb-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden"
                    >
                      {rowsOptions.map((v) => (
                        <button
                          key={v}
                          onClick={() => {
                            setRowsPerPage(v);
                            setShowRowsDropdown(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                            rowsPerPage === v
                              ? "bg-red-50 text-red-600 font-medium"
                              : "text-gray-700"
                          }`}
                        >
                          {v} per page
                          {rowsPerPage === v && (
                            <FaCheck className="w-3 h-3" />
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <span className="text-sm text-gray-400">
                {(page - 1) * rowsPerPage + 1}–
                {Math.min(page * rowsPerPage, filtered.length)} of{" "}
                {filtered.length}
              </span>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleChangePage(1)}
                  disabled={page === 1}
                  className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                  <TbChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleChangePage(page - 1)}
                  disabled={page === 1}
                  className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                  <TbChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (page <= 3) pageNum = i + 1;
                  else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = page - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handleChangePage(pageNum)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                        page === pageNum
                          ? "bg-[#6366F1] text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handleChangePage(page + 1)}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                  <TbChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleChangePage(totalPages)}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                  <TbChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Confirm Delete Modal */}
      <AnimatePresence>
        {confirmClient && (
          <ConfirmDeleteModal
            client={confirmClient}
            onConfirm={() => handleDelete(confirmClient)}
            onCancel={() => setConfirmClient(null)}
          />
        )}
      </AnimatePresence>

      {/* Add to excluded modal */}
      <AnimatePresence>
        {showAddModal && (
          <ExcludeClientsModal
            onClose={() => setShowAddModal(false)}
            onSuccess={() => dispatch(getExcludeProfilesThunk())}
          />
        )}
      </AnimatePresence>
    </motion.main>
  );
}
